/**
 * Page families (MODULE-AUDIT section 5, "merge duplicates into tabs one
 * level down"). The sidebar keeps one entry per family (Checkout, Documents,
 * Recall, Outreach, Lab & referrals, ...); the shell draws the rest as tabs
 * above the page, so Close the day, Scan inbox, Letters, Recall results,
 * Benefits campaign and Referrals stay one click away without their own
 * sidebar rows. Pages with their own tab bars (Analytics, Claims, Settings)
 * are not listed here.
 *
 * The Schedule is not a family (owner 2026-10-21: "get rid of" the
 * Schedule | All offices | Call list | Productivity | Light Board row): the
 * page starts with its date toolbar, and those pages are "Other views" in
 * the schedule's menu (modules/schedule/panels/otherViews.ts), Ctrl+K and
 * All modules.
 */
export interface FamilyTab {
  path: string;
  label: string;
  /** The page fills the viewport: the tabs take their row from it. */
  fills?: boolean;
}

export interface PageFamily {
  id: string;
  label: string;
  tabs: readonly FamilyTab[];
}

export const PAGE_FAMILIES: readonly PageFamily[] = [
  {
    id: 'checkout',
    label: 'Checkout',
    tabs: [
      { path: '/frontdesk', label: 'Checkout' },
      { path: '/frontdesk/close', label: 'Close the day' },
    ],
  },
  {
    id: 'documents',
    label: 'Documents',
    tabs: [
      { path: '/frontdesk/patient', label: 'Documents' },
      { path: '/frontdesk/scan-inbox', label: 'Scan inbox' },
      { path: '/letters', label: 'Letters and labels' },
    ],
  },
  {
    id: 'recall',
    label: 'Recall',
    tabs: [
      { path: '/recall', label: 'Recall' },
      { path: '/care-outreach/recall-results', label: 'Results' },
    ],
  },
  {
    id: 'outreach',
    label: 'Outreach',
    tabs: [
      { path: '/campaigns', label: 'Campaigns' },
      { path: '/care-outreach/benefits-campaign', label: 'Benefits campaign' },
    ],
  },
  {
    // Release r8 (TC role test): the case-acceptance report and the
    // predeterminations waiting are tabs of Treatment plans.
    id: 'txplan',
    label: 'Treatment plans',
    tabs: [
      { path: '/treatment-plans', label: 'Treatment plans' },
      { path: '/treatment-plans/acceptance', label: 'Case acceptance' },
      { path: '/treatment-plans/predeterminations', label: 'Predeterminations' },
      { path: '/case-closed', label: 'Case Closed' },
    ],
  },
  {
    // Owner feedback 2026-10-21: fee schedules and insurance plans left
    // Settings for their own module under Billing & insurance.
    id: 'insurance-fees',
    label: 'Insurance & fees',
    tabs: [
      { path: '/insurance-fees', label: 'Insurance plans' },
      { path: '/insurance-fees/carriers', label: 'Carriers' },
      { path: '/insurance-fees/fee-schedules', label: 'Fee schedules' },
      { path: '/insurance-fees/ppo-analysis', label: 'PPO analysis' },
      { path: '/insurance-fees/coverage', label: 'Coverage' },
      { path: '/insurance-fees/frequency-limits', label: 'Frequency limits' },
    ],
  },
  {
    id: 'lab',
    label: 'Lab & referrals',
    tabs: [
      { path: '/lab', label: 'Lab cases' },
      { path: '/referrals', label: 'Referrals' },
    ],
  },
];

/**
 * The family whose tab is exactly `pathname`, with only the tabs the person
 * can open (`canOpen`), or undefined when fewer than two would show.
 */
export function familyFor(
  pathname: string,
  canOpen: (path: string) => boolean,
): { family: PageFamily; tabs: FamilyTab[]; active: string } | undefined {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const family = PAGE_FAMILIES.find((f) => f.tabs.some((t) => t.path === path));
  if (!family) return undefined;
  const tabs = family.tabs.filter((t) => t.path === path || canOpen(t.path));
  if (tabs.length < 2) return undefined;
  return { family, tabs, active: path };
}
